/**
 * The verification flowdown: campaign, plan, case.
 *
 * The structure on this screen is DERIVED, not stored, and that is the whole
 * reason it is worth writing down here.
 *
 * `GET /api/verification` answers `verification_kind` and `is_definition` but
 * the containment edge is not a column a table can just show, so each level is
 * rebuilt from two facts the listing does carry:
 *
 *   1. containment - a usage's qualified name is its container's plus its own
 *      name, so dropping the last `::` segment names the element it sits in;
 *   2. type resolution - that usage is TYPED by the definition of the next
 *      level down, so its `type_name` has to be resolved to a definition.
 *
 * That is TWO steps per level, because BALROG routes the flowdown through
 * usages rather than definitions: a campaign definition contains a plan usage,
 * whose type is a plan definition, which contains case usages, whose types are
 * case definitions. It is faithful SysML and not a shape to flatten away - so
 * a node shows both the usage that routed to it and the definition it landed
 * on, and a type that resolves to nothing says so rather than vanishing.
 *
 * A case that no plan uses is LISTED ON ITS OWN rather than dropped. That
 * omission is the finding this screen exists to surface; hiding it would turn
 * a gap in the model into a gap in the tool.
 */

import { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView'
import { TreeItem } from '@mui/x-tree-view/TreeItem'
import { ApiError, PAGE_LIMIT, api } from '../api'
import type { VerificationRow } from '../schema'
import { MONOSPACE } from '../theme'
import EmptyState from '../components/common/EmptyState'
import EntityFilterBar from '../components/entity/EntityFilterBar'
import { matchesTerms, readSearch, writeSearch } from '../components/entity/rowFilter'
import { useRememberedFilters } from '../components/entity/rememberedFilters'

/**
 * One node of the derived tree.
 *
 * Both ends are kept. `usage` is how the parent reached this node and carries
 * the name written at the call site; `definition` is what the usage's type
 * resolved to and carries the ID, the statement and the requirement verified.
 * A root is a definition with no usage above it; an unresolved type is a usage
 * with no definition below it, which is a real state of the model.
 */
export interface VerificationNode {
  /** Unique within the tree, so the same definition may appear under two plans. */
  key: string
  usage: VerificationRow | null
  definition: VerificationRow | null
  /** The `type_name` that resolved to nothing, or why it could not be resolved. */
  unresolved: string
  children: VerificationNode[]
}

/**
 * The whole derived structure, including everything the tree could not reach.
 *
 * The four lists are exhaustive over the listing: an element is under a
 * campaign, or in one of the leftovers, and never nowhere.
 */
export interface VerificationTree {
  campaigns: VerificationNode[]
  /** Plan definitions no campaign uses. */
  uncampaignedPlans: VerificationNode[]
  /** Case definitions no plan uses - the omission worth seeing. */
  unplannedCases: VerificationNode[]
  /** Usages declared inside something this listing does not contain. */
  strayUsages: VerificationRow[]
}

/** The container of a qualified name: everything before the last `::`. */
export function parentQualifiedName(qualifiedName: string): string {
  const cut = qualifiedName.lastIndexOf('::')
  return cut === -1 ? '' : qualifiedName.slice(0, cut)
}

/**
 * The last `::` segment of a reference.
 *
 * A usage's type arrives as written - `TC::STR::MaxStressCase` - where the
 * leading segments are package short names this endpoint does not resolve. The
 * last segment is the declared name of the definition, which is the part that
 * can be matched against what the listing carries.
 */
export function localName(reference: string): string {
  const cut = reference.lastIndexOf('::')
  return cut === -1 ? reference : reference.slice(cut + 2)
}

/**
 * Rebuild campaign -> plan -> case from containment and type resolution.
 *
 * Exported and tested directly: every edge here is inferred, so a wrong answer
 * renders a perfectly plausible tree. The two mistakes worth guarding are
 * matching `type_name` whole (nothing resolves, and the tree silently becomes
 * a list of campaigns) and dropping what the walk did not reach (the model's
 * gaps disappear along with the noise).
 *
 * @param rows every verification element, as `GET /api/verification` answers
 */
export function buildVerificationTree(rows: VerificationRow[]): VerificationTree {
  const definitions = rows.filter((row) => row.is_definition)
  const usages = rows.filter((row) => !row.is_definition)

  const byQualifiedName = new Map<string, VerificationRow>()
  for (const definition of definitions) byQualifiedName.set(definition.qualified_name, definition)

  const byIdentifier = new Map<string, VerificationRow>()
  for (const definition of definitions) {
    if (definition.id) byIdentifier.set(definition.id, definition)
  }

  // Candidates per declared name, not a single winner: two definitions of the
  // same name in different packages cannot be told apart from a type written
  // with package short names, and picking one at random would be a guess
  // presented as a fact.
  const byLocalName = new Map<string, VerificationRow[]>()
  for (const definition of definitions) {
    const name = localName(definition.qualified_name)
    byLocalName.set(name, [...(byLocalName.get(name) ?? []), definition])
  }

  const usagesInside = new Map<string, VerificationRow[]>()
  for (const usage of usages) {
    // Containment first, per the qualified name. `owner` is a derived
    // REFERENCE naming the same edge by identifier, and is used only where
    // containment finds nothing - a definition may sit in a package the
    // listing does not describe.
    const container = parentQualifiedName(usage.qualified_name)
    const owner = typeof usage.owner === 'string' ? usage.owner : ''
    const key = byQualifiedName.has(container)
      ? container
      : (byIdentifier.get(owner)?.qualified_name ?? '')
    if (!key) continue
    usagesInside.set(key, [...(usagesInside.get(key) ?? []), usage])
  }

  const reached = new Set<string>()

  /** The definition a usage's type names, or why it could not be named. */
  const resolve = (usage: VerificationRow): { definition: VerificationRow | null; why: string } => {
    const written = String(usage.type_name ?? '')
    if (!written) return { definition: null, why: 'the usage states no type' }
    const candidates = byLocalName.get(localName(written)) ?? []
    if (candidates.length === 1) return { definition: candidates[0], why: '' }
    if (candidates.length === 0) {
      return { definition: null, why: `${written} resolves to no definition in this program` }
    }
    return {
      definition: null,
      why: `${written} matches ${candidates.length} definitions (${candidates
        .map((row) => row.qualified_name)
        .join(', ')})`,
    }
  }

  /** Expand one definition's usages, refusing to follow a cycle back up. */
  const expand = (definition: VerificationRow, path: string[]): VerificationNode[] =>
    (usagesInside.get(definition.qualified_name) ?? []).map((usage) => {
      const { definition: target, why } = resolve(usage)
      const looping = target !== null && path.includes(target.qualified_name)
      if (target) reached.add(target.qualified_name)
      return {
        key: usage.qualified_name,
        usage,
        definition: target,
        unresolved: looping
          ? `${usage.type_name} is already above this node, so the walk stops here`
          : why,
        children: target && !looping ? expand(target, [...path, target.qualified_name]) : [],
      }
    })

  /** A definition as a root node of its own. */
  const rootOf = (definition: VerificationRow): VerificationNode => ({
    key: definition.qualified_name,
    usage: null,
    definition,
    unresolved: '',
    children: expand(definition, [definition.qualified_name]),
  })

  const campaigns = definitions
    .filter((row) => row.verification_kind === 'campaign')
    .map(rootOf)

  // Plans and then cases, in that order: expanding an unused plan still marks
  // its cases as reached, so a case under a plan no campaign runs is reported
  // as an uncampaigned PLAN rather than as three separate unplanned cases.
  const uncampaignedPlans = definitions
    .filter((row) => row.verification_kind === 'plan' && !reached.has(row.qualified_name))
    .map(rootOf)

  const unplannedCases = definitions
    .filter((row) => row.verification_kind === 'case' && !reached.has(row.qualified_name))
    .map(rootOf)

  const attached = new Set<string>()
  for (const inside of usagesInside.values()) {
    for (const usage of inside) attached.add(usage.qualified_name)
  }
  const strayUsages = usages.filter((usage) => !attached.has(usage.qualified_name))

  return { campaigns, uncampaignedPlans, unplannedCases, strayUsages }
}

/** What a node's text is searched against: both names, the ID and the type. */
function haystack(node: VerificationNode): string {
  const parts = [
    node.usage?.name,
    node.usage?.id,
    node.usage?.type_name,
    node.definition?.name,
    node.definition?.id,
    node.definition?.statement,
    node.definition?.verifies,
  ]
  return parts.map((part) => String(part ?? '')).join(' ').toLowerCase()
}

/** Keep a node when it or anything below it matches, as Flowdown does. */
export function pruneNodes(nodes: VerificationNode[], text: string): VerificationNode[] {
  const keep = (node: VerificationNode): VerificationNode | null => {
    const children = node.children.map(keep).filter(Boolean) as VerificationNode[]
    // Shared term rule: every term must hold, in any order. Was a single
    // substring test, so 'gnc unverified' found nothing here and rows elsewhere.
    if (matchesTerms(haystack(node), text) || children.length) return { ...node, children }
    return null
  }
  return nodes.map(keep).filter(Boolean) as VerificationNode[]
}

/** Every key in a set of nodes, for expanding the whole tree at once. */
function allKeys(nodes: VerificationNode[]): string[] {
  return nodes.flatMap((node) => [node.key, ...allKeys(node.children)])
}

const HEADER =
  'Structure is derived rather than stored: each level is a usage declared ' +
  'inside its parent (read from the qualified name) whose type resolves to ' +
  'the definition of the level below. Campaign definition, plan usage, plan ' +
  'definition, case usages, case definitions - two steps per level, because ' +
  'the model routes the flowdown through usages rather than definitions.'

export default function VerificationFlowdown() {
  const [rows, setRows] = useState<VerificationRow[] | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  /**
   * The search, in the URL under `search` as on every other list (issue #86).
   *
   * It was component state, so it was gone the moment anything else was opened
   * and the box was empty on the way back. In the URL it is a link, and
   * remembered so that the sidebar and the breadcrumbs put it back. Replace, not
   * push: typing must not fill up the back button.
   */
  const [params, setParams] = useSearchParams()
  const search = readSearch(params)
  const setSearch = (next: string) =>
    setParams((now) => writeSearch(now, next), { replace: true })
  useRememberedFilters()
  /** What the server says exists, against what it was allowed to send. */
  const [total, setTotal] = useState(0)

  /**
   * Every case, not the first thousand.
   *
   * The filter below runs in the browser, so a capped fetch would search only
   * what was fetched and answer "nothing matches" for cases that exist - the
   * failure the requirements screen warned about in its own header while this
   * one quietly did it. PAGE_LIMIT is the API's ceiling, and `truncated` says
   * so on the rare occasion it is reached rather than implying completeness.
   */
  useEffect(() => {
    api
      .verification({ limit: PAGE_LIMIT })
      .then((page) => {
        setRows(page.items)
        setTotal(page.total)
      })
      .catch((failure: ApiError) => setError(failure))
  }, [])

  const tree = useMemo(
    () =>
      rows
        ? buildVerificationTree(rows)
        : { campaigns: [], uncampaignedPlans: [], unplannedCases: [], strayUsages: [] },
    [rows],
  )

  const sections = useMemo(
    () =>
      [
        {
          key: 'campaigns',
          title: 'Campaigns',
          note: 'A campaign, the plans it runs, and the cases those plans run.',
          nodes: search ? pruneNodes(tree.campaigns, search) : tree.campaigns,
        },
        {
          key: 'plans',
          title: 'Plans no campaign runs',
          note:
            'These plans are declared and complete, but no campaign contains a usage of ' +
            'them, so nothing schedules them.',
          nodes: search ? pruneNodes(tree.uncampaignedPlans, search) : tree.uncampaignedPlans,
        },
        {
          key: 'cases',
          title: 'Cases no plan runs',
          note:
            'Listed here rather than dropped: a case that no plan contains is the ' +
            'omission this screen exists to show, not noise to hide.',
          nodes: search ? pruneNodes(tree.unplannedCases, search) : tree.unplannedCases,
        },
      ].filter((section) => section.nodes.length > 0),
    [tree, search],
  )

  if (error) return <Alert severity="error">{error.message}</Alert>
  if (!rows) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  const nothingShown = sections.length === 0 && tree.strayUsages.length === 0

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5">Verification flowdown</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '90ch' }}>
          {HEADER}
        </Typography>
      </Box>

      <EntityFilterBar
        plural="cases"
        value={search}
        onChange={setSearch}
        hint="ancestors kept"
      />
      {total > rows.length && (
        <Typography variant="body2" color="warning.main">
          Showing the first {rows.length} of {total}. The filter searches only these.
        </Typography>
      )}

      {nothingShown ? (
        <Paper sx={{ p: 2 }}>
          <EmptyState
            title={
              search
                ? `Nothing matches “${search}”`
                : 'This program models no verification elements'
            }
            description={
              search
                ? undefined
                : 'A campaign, a plan and a case would fill this screen. Declare them in ' +
                  'the model and import it again.'
            }
          />
        </Paper>
      ) : (
        sections.map((section) => (
          <Paper key={section.key} sx={{ p: 2 }}>
            <Typography variant="subtitle1">{section.title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: '90ch' }}>
              {section.note}
            </Typography>
            <SimpleTreeView
              key={search}
              defaultExpandedItems={allKeys(section.nodes)}
              sx={{ '& .MuiTreeItem-content': { py: 0.35 } }}
            >
              {section.nodes.map((node) => (
                <Branch key={node.key} node={node} />
              ))}
            </SimpleTreeView>
          </Paper>
        ))
      )}

      {tree.strayUsages.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1">Usages with no container in this listing</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: '90ch' }}>
            Each of these is declared inside something this listing does not contain — most
            often a shared library definition, which is not this program’s to change. They are
            shown rather than attached to a guess.
          </Typography>
          <Stack spacing={0.5}>
            {tree.strayUsages.map((usage) => (
              <Typography key={usage.qualified_name} variant="body2" sx={{ fontFamily: MONOSPACE }}>
                {usage.qualified_name}
                {usage.type_name ? ` : ${usage.type_name}` : ''}
              </Typography>
            ))}
          </Stack>
        </Paper>
      )}
    </Stack>
  )
}

/** The chip colour each kind gets, so a level is readable at a glance. */
const KIND_COLOUR: Record<string, 'primary' | 'secondary' | 'default'> = {
  campaign: 'primary',
  plan: 'secondary',
  case: 'default',
}

function Branch({ node }: { node: VerificationNode }) {
  const { usage, definition } = node
  const kind = String(definition?.verification_kind ?? usage?.verification_kind ?? '')

  return (
    <TreeItem
      itemId={node.key}
      label={
        <Stack spacing={0.25} sx={{ minWidth: 0, py: 0.25 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
            {kind && (
              <Chip
                label={kind}
                size="small"
                variant="outlined"
                color={KIND_COLOUR[kind] ?? 'default'}
                sx={{ height: 20 }}
              />
            )}

            {/* The usage, when there is one: the name written at the call site
                and the type as written, which is the edge being derived. */}
            {usage && (
              <Typography variant="body2" sx={{ fontFamily: MONOSPACE }}>
                {usage.name || usage.id}
              </Typography>
            )}
            {usage && definition && (
              <Typography variant="body2" color="text.secondary">
                →
              </Typography>
            )}

            {definition && (
              <>
                {definition.id ? (
                  <RouterLink
                    to={`/verification/${encodeURIComponent(definition.id)}`}
                    style={{ fontFamily: MONOSPACE, fontSize: '0.85rem' }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    {definition.id}
                  </RouterLink>
                ) : null}
                <Typography variant="body2">{definition.name}</Typography>
              </>
            )}

            {definition?.verifies && (
              <Chip
                label={`verifies ${definition.verifies}`}
                size="small"
                variant="outlined"
                sx={{ height: 20 }}
              />
            )}
            {definition?.is_illustrative === true && (
              <Chip label="Illustrative" size="small" color="warning" sx={{ height: 20 }} />
            )}
          </Stack>

          {node.unresolved && (
            <Typography variant="caption" color="warning.main">
              {node.unresolved}
            </Typography>
          )}
          {definition?.statement ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {String(definition.statement)}
            </Typography>
          ) : null}
        </Stack>
      }
    >
      {node.children.map((child) => (
        <Branch key={child.key} node={child} />
      ))}
    </TreeItem>
  )
}
